import type { SxProps } from "@mui/material";

// Formatação por célula opcional (ex.: semáforo de conversão): recebe o valor cru da célula, o nó da
// linha (`node`; undefined na linha Total) e `isTotalsRow`, e devolve um sx MUI ou undefined para não
// colorir (ex.: célula "—"). O `node`/`isTotalsRow` permitem colorir só certas linhas (ex.: colorir o
// total e as linhas de topo de certos centros, deixando outras neutras). DEVE devolver um objeto de
// estilo plano (sem callback-de-tema nem arrays — o PivotTableCard funde-o por spread, tal como o
// getRowSx do DataTableCard). O PivotTableCard aplica-o a linhas normais E à linha Total.
export type PivotCellSx = (rawValue: number | null, node?: PivotNode<unknown>, isTotalsRow?: boolean) => SxProps | undefined;

export type PivotColumn = {
    key: string;
    label: string;
    isNumeric: boolean;
    cellSx?: PivotCellSx;
};

export type PivotMeasure<ItemType> = {
    key: string;
    label: string;
    isNumeric?: boolean;
    compute: (cellItems: Array<ItemType>, allItems: Array<ItemType>) => number | null;
    format: (value: number | null) => string;
    cellSx?: PivotCellSx;
};

export type PivotNode<ItemType> = {
    label: string;
    depth: number;
    path: Array<string>;
    items: Array<ItemType>;
    itemsByColumn?: Record<string, Array<ItemType>>;
    formattedByColumn: Record<string, string>;
    rawByColumn: Record<string, number | null>;
    children: Array<PivotNode<ItemType>>;
};

export type PivotResult<ItemType> = {
    columns: Array<PivotColumn>;
    rows: Array<PivotNode<ItemType>>;
    totals: PivotNode<ItemType>;
};

// Coluna derivada dos valores já calculados da linha (ex.: a mediana dos meses), colocada DEPOIS da
// coluna Total. Só existe no modo com dimensão de coluna; no modo medidas é ignorada. O `label` é
// próprio — nunca passa pelo `columnLabelOf`, que só sabe traduzir chaves de coluna reais.
export type PivotSummaryColumn = {
    key: string;
    label: string;
    compute: (nodeValues: { columnKeys: Array<string>; rawByColumn: Record<string, number | null> }) => number | null;
    format: (value: number | null) => string;
    cellSx?: PivotCellSx;
};

export type BuildPivotOptions<ItemType> = {
    items: Array<ItemType>;
    rowLevels: Array<(item: ItemType) => string>;
    measures: Array<PivotMeasure<ItemType>>;
    columnKeyOf?: (item: ItemType) => string;
    columnLabelOf?: (columnKey: string) => string;
    totalColumnLabel?: string;
    summaryColumn?: PivotSummaryColumn;
    sortRowsBy?: Array<(first: PivotNode<ItemType>, second: PivotNode<ItemType>) => number>;
};

export const TOTAL_COLUMN_KEY = "__total";

const groupItemsBy = <ItemType,>(items: Array<ItemType>, keyOf: (item: ItemType) => string) => {
    const groups = new Map<string, Array<ItemType>>();
    items.forEach((item) => {
        const key = keyOf(item);
        const groupItems = groups.get(key) || [];
        groupItems.push(item);
        groups.set(key, groupItems);
    });
    return groups;
};

const byLabelAscending = <ItemType,>(first: PivotNode<ItemType>, second: PivotNode<ItemType>) => first.label.localeCompare(second.label);

export const buildPivot = <ItemType,>({
    items,
    rowLevels,
    measures,
    columnKeyOf,
    columnLabelOf,
    totalColumnLabel,
    summaryColumn,
    sortRowsBy,
}: BuildPivotOptions<ItemType>): PivotResult<ItemType> => {
    const usesColumnDimension = Boolean(columnKeyOf);
    const columnKeys = usesColumnDimension ? Array.from(new Set(items.map((item) => columnKeyOf!(item)))).sort() : [];

    const columns: Array<PivotColumn> = usesColumnDimension
        ? [
              ...columnKeys.map((columnKey) => ({
                  key: columnKey,
                  label: columnLabelOf ? columnLabelOf(columnKey) : columnKey,
                  isNumeric: measures[0].isNumeric ?? true,
                  cellSx: measures[0].cellSx,
              })),
              { key: TOTAL_COLUMN_KEY, label: totalColumnLabel || "Total", isNumeric: measures[0].isNumeric ?? true, cellSx: measures[0].cellSx },
              ...(summaryColumn
                  ? [{ key: summaryColumn.key, label: summaryColumn.label, isNumeric: measures[0].isNumeric ?? true, cellSx: summaryColumn.cellSx }]
                  : []),
          ]
        : measures.map((measure) => ({ key: measure.key, label: measure.label, isNumeric: measure.isNumeric ?? true, cellSx: measure.cellSx }));

    const computeNodeValues = (nodeItems: Array<ItemType>) => {
        const formattedByColumn: Record<string, string> = {};
        const rawByColumn: Record<string, number | null> = {};
        let itemsByColumn: Record<string, Array<ItemType>> | undefined;

        if (usesColumnDimension) {
            const measure = measures[0];
            const groupedByColumn = groupItemsBy(nodeItems, columnKeyOf!);
            itemsByColumn = {};
            columnKeys.forEach((columnKey) => {
                const cellItems = groupedByColumn.get(columnKey);
                if (cellItems) itemsByColumn![columnKey] = cellItems;
                const rawValue = cellItems ? measure.compute(cellItems, items) : null;
                rawByColumn[columnKey] = rawValue;
                formattedByColumn[columnKey] = cellItems ? measure.format(rawValue) : "";
            });
            const totalRaw = measure.compute(nodeItems, items);
            rawByColumn[TOTAL_COLUMN_KEY] = totalRaw;
            formattedByColumn[TOTAL_COLUMN_KEY] = measure.format(totalRaw);
            if (summaryColumn) {
                const summaryRaw = summaryColumn.compute({ columnKeys, rawByColumn });
                rawByColumn[summaryColumn.key] = summaryRaw;
                formattedByColumn[summaryColumn.key] = summaryColumn.format(summaryRaw);
            }
        } else {
            measures.forEach((measure) => {
                const rawValue = measure.compute(nodeItems, items);
                rawByColumn[measure.key] = rawValue;
                formattedByColumn[measure.key] = measure.format(rawValue);
            });
        }

        const result = { formattedByColumn, rawByColumn, itemsByColumn };
        return result;
    };

    const buildLevelNodes = (levelItems: Array<ItemType>, depth: number, parentPath: Array<string>): Array<PivotNode<ItemType>> => {
        if (depth >= rowLevels.length) return [];
        const groups = groupItemsBy(levelItems, rowLevels[depth]);
        const nodes = Array.from(groups.entries()).map(([label, groupItems]) => {
            const path = [...parentPath, label];
            const node: PivotNode<ItemType> = {
                label,
                depth,
                path,
                items: groupItems,
                ...computeNodeValues(groupItems),
                children: buildLevelNodes(groupItems, depth + 1, path),
            };
            return node;
        });
        const comparator = sortRowsBy?.[depth] || byLabelAscending;
        nodes.sort(comparator);
        return nodes;
    };

    const totals: PivotNode<ItemType> = {
        label: "Total",
        depth: 0,
        path: ["Total"],
        items,
        ...computeNodeValues(items),
        children: [],
    };

    const result: PivotResult<ItemType> = { columns, rows: buildLevelNodes(items, 0, []), totals };
    return result;
};
